import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applicationsApi } from "./api";
import { Button, Card, Input, PageTitle, StatusBadge } from "../../components/ui";
import { QueryView } from "../../components/states";
import { formatDateRange } from "../../lib/datetime";

export function CompanyInboxPage() {
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const query = useQuery({
    queryKey: ["applications", "company"],
    queryFn: () => applicationsApi.list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["applications"] });

  const approve = useMutation({
    mutationFn: (id: string) => applicationsApi.approve(id),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      applicationsApi.reject(id, reason || undefined),
    onSuccess: () => {
      setRejectingId(null);
      setReason("");
      invalidate();
    },
  });

  return (
    <div className="space-y-6">
      <PageTitle title="申込を確認" subtitle="学校からの受入申込を承認・却下できます。" />
      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="まだ申込はありません。"
      >
        {(data) => (
          <div className="space-y-3">
            {data.items.map((a) => (
              <Card key={a.id} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-extrabold text-brand-dark">{a.schoolName}</h3>
                    <StatusBadge status={a.status} />
                  </div>
                  <span className="text-xs text-slate-400">
                    {formatDateRange(a.startDate, a.endDate)}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {a.headcount}人・希望職種: {a.desiredJobType}
                </p>
                <p className="text-xs text-slate-400">
                  受入可能人数（一度の上限）: {a.companyCapacity}人
                  {a.status === "applied" && (
                    <>
                      {" "}
                      / 同時期の受入予定（承認済）{a.committedHeadcount}人 ＋ 本申込 {a.headcount}人
                      ＝{" "}
                      <span
                        className={
                          a.committedHeadcount + a.headcount > a.companyCapacity
                            ? "font-bold text-amber-600"
                            : "font-bold text-slate-600"
                        }
                      >
                        {a.committedHeadcount + a.headcount}人
                      </span>
                      {a.committedHeadcount + a.headcount > a.companyCapacity && (
                        <span className="ml-1 font-bold text-amber-600">
                          ⚠ 承認すると同時期の受入が上限を超えます（承認可否はご判断ください）
                        </span>
                      )}
                      {/* 保留中の他申込も含めた見込み。1件目の承認時にも「殺到」に気づけるように */}
                      {a.pendingHeadcount > 0 && (
                        <span className="block text-slate-400">
                          （同時期に承認待ちの他申込が {a.pendingHeadcount}人分あります。すべて承認
                          すると {a.committedHeadcount + a.headcount + a.pendingHeadcount}人）
                        </span>
                      )}
                    </>
                  )}
                </p>
                {a.note && <p className="text-xs text-slate-400">連絡事項: {a.note}</p>}

                {a.status === "applied" && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button onClick={() => approve.mutate(a.id)} disabled={approve.isPending}>
                      承認する
                    </Button>
                    {rejectingId === a.id ? (
                      <div className="flex flex-1 flex-wrap items-center gap-2">
                        <Input
                          placeholder="却下理由（任意）"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          className="!w-64"
                        />
                        <Button
                          variant="danger"
                          onClick={() => reject.mutate({ id: a.id, reason })}
                          disabled={reject.isPending}
                        >
                          却下を確定
                        </Button>
                        <Button variant="ghost" onClick={() => setRejectingId(null)}>
                          やめる
                        </Button>
                      </div>
                    ) : (
                      <Button variant="ghost" onClick={() => setRejectingId(a.id)}>
                        却下する
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </QueryView>
    </div>
  );
}
